import { divide, dotDivide, dotMultiply, multiply, subtract, sum } from 'mathjs';

/**
 * createPoliciesInForce - return decile policies in force
 * @param {number} policies - total numbers of policies in force
 * @param {boolean} isPersonalAuto
 * @return {number[]}
 */
export function createPoliciesInForce(policies, isPersonalAuto = false) {
  const multiplier = +isPersonalAuto + 1;
  return new Array(10).fill((multiplier * policies) / 10);
}

/**
 * correctMeanPremiumPerPolicy
 * @param {number[]} avgPremiumPerDecile
 * @param {number} fallbackAvgPremium
 * @param {boolean} isPersonalAuto
 * @return {number[]}
 */
export function correctMeanPremiumPerPolicy(avgPremiumPerDecile, fallbackAvgPremium, isPersonalAuto = false) {
  const multiplier = +isPersonalAuto + 1;
  if (avgPremiumPerDecile) return multiply(avgPremiumPerDecile, multiplier);
  if (!avgPremiumPerDecile && fallbackAvgPremium) return multiply(new Array(10).fill(fallbackAvgPremium), multiplier);
  return new Array(10).fill(0);
}

/**
 * createCancellationLadder - calculate the dropped policies per decile, spilling excess dropped policies to the next bucket
 * @param {number[]} maxPerDecile
 * @return {function(number[], number, number, number[]): (number[])}
 */
export const createCancellationLadder = (maxPerDecile) => (acc, nonRenew, idx, nonRenewalDistribution) => {
  const lastNonRenew = nonRenewalDistribution[idx - 1]; // last decile's non-renewal
  const maxDrop = maxPerDecile[9 - idx] * 100 || Number.POSITIVE_INFINITY; // current decile's max cancellable
  const lastMaxDrop = (maxPerDecile[10 - idx] ?? 0) * 100; // current decile's max cancellable
  if (idx === 0) {
    // NOTE: first calc uses only current decile info
    acc.push(Math.min(nonRenew, maxDrop) / 100);
    return acc;
  }
  const lastOverflow = acc.at(-1) * 100;
  const lastOverflowDrop = Math.max(lastNonRenew, lastOverflow); // last non-renew or result whichever is larger
  const lastCorDrop = Math.min(lastOverflow, lastMaxDrop); // last max cancels or result whichever is smaller
  const end = nonRenew + lastOverflowDrop - lastCorDrop;
  acc.push(end / 100);
  return acc.with(-2, lastCorDrop / 100);
};

/**
 * calculate the dropped policies per decile for the current year
 * @param {number} nonRenewalFrac - fractional non-renewals for this year (0-1)
 * @param {YearDetail} previousYear - values from the previous year
 * @param {number[]} nonRenewalDistribution - non-renewal values per decile (0-100)
 * @return {{}}
 */
export function calculateYearlyDroppedPolicies(nonRenewalFrac, previousYear, nonRenewalDistribution) {
  const result = {};
  result.expectedDrop = nonRenewalFrac * previousYear.sumPolicies;
  result.maxDropPerDecile = divide(previousYear.policies, result.expectedDrop).map((val) => {
    const trial = Math.min(val, 1);
    return Number.isFinite(trial) ? trial : 1;
  });
  result.finalDrop = nonRenewalDistribution
    .toReversed()
    .reduce(createCancellationLadder(result.maxDropPerDecile), [])
    .toReversed()
    .map((rate) => rate * nonRenewalFrac * previousYear.sumPolicies);
  return result;
}

/**
 * calculateYearlyValues
 * @param {YearDetail} previousYear - previous year's values
 * @param {math.Matrix|number[]} policies - policies per decile
 * @param {number[]} avgPremium - average premium per decile
 * @return {YearDetail} - current year's values
 */
export function calculateYearlyValues(policies, previousYear, avgPremium) {
  const sumPolicies = sum(policies);
  const premium = dotMultiply(policies, avgPremium);
  const sumPremium = sum(premium);
  const loss = dotMultiply(premium, previousYear.lr);
  const sumLoss = sum(loss);
  const net = subtract(premium, loss);
  const sumNet = sum(net);
  const lr = dotDivide(loss, premium).map((val, idx) => (Number.isFinite(val) ? val : previousYear.lr[idx]));
  const totalLR = sumLoss / sumPremium;
  return {
    policies,
    sumPolicies,
    premium,
    sumPremium,
    loss,
    sumLoss,
    net,
    sumNet,
    lr,
    totalLR,
  };
}

/**
 * simplifyTypeDetail - shorten type detail based on content
 * @param {string} typeDetail
 * @return {string}
 */
export function simplifyTypeDetail(typeDetail) {
  if (typeDetail.includes('PERSONAL AUTO')) return 'Personal Auto';
  if (typeDetail.includes('HOME')) return 'Homeowners';
  if (typeDetail.includes('COMMERCIAL AUTO')) return 'Commercial Auto';
  if (typeDetail.includes('WORKER')) return "Workers' Comp";
  if (typeDetail.includes('GENERAL')) return 'General Liability';
  return 'Other';
}
